import { inject, Pipe, PipeTransform } from '@angular/core'
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser'
import DOMPurify from 'dompurify'
/**
 * Safe pipe. Examples of usage:
 * <p [innerHTML]="userHtml | gbSafe: 'html'"></p>
 * <p [innerHTML]="internalHtml | gbSafe: 'trustedHtml'"></p>
 * <iframe [src]="catVideoEmbed | gbSafe: 'resourceUrl'"></iframe>
 */

@Pipe({
  name: 'gbSafe',
  pure: true,
})
export class GbSafePipe implements PipeTransform {
  sanitizer = inject(DomSanitizer)

  constructor() {
    this.registerIframeRestrictionHook()
  }

  transform(value: string | undefined, type: 'html' | 'resourceUrl' | 'trustedHtml'): SafeHtml | SafeResourceUrl {
    if (!value) return ''

    switch (type) {
      case 'html':
        // sanitize HTML using DOMPurify for user generated content
        return this.sanitizer.bypassSecurityTrustHtml(this.sanitizeHtml(value))
      case 'trustedHtml':
        // bypass DOMPurify entirely for internal content (e.g. CMS table-cell content)
        return this.sanitizer.bypassSecurityTrustHtml(value)
      case 'resourceUrl':
        return this.sanitizer.bypassSecurityTrustResourceUrl(value)
      default:
        throw new Error(`Invalid safe type specified: ${type}`)
    }
  }

  private sanitizeHtml(value: string): string {
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [
        'b',
        'i',
        'u',
        'strong',
        'em',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'a',
        'img',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'span',
        'iframe',
        's',
        'del',
        'strike',
      ],
      ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'target',
        'class',
        'style',
        'width',
        'height',
        'frameborder',
        'allowfullscreen',
        'allow',
      ],
      FORBID_ATTR: ['onerror', 'onclick', 'onload'], // prevent XSS via event handlers
    })
  }

  /**
   * Applies global hook for <iframe> for user-generated HTML sanitized with DOMPurify
   */
  private registerIframeRestrictionHook(): void {
    // only allow YouTube, Vimeo, and Instagram iframe src
    const iframeUriRegexp =
      /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|player\.vimeo\.com|instagram\.com)\//

    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
      if (data.attrName === 'src' && node.nodeName === 'IFRAME') {
        if (!iframeUriRegexp.test(data.attrValue)) {
          data.keepAttr = false // remove untrusted src
        }
      }
    })
  }
}
